/**
 * Display helpers for the three work-order durations (#2705).
 *
 * Planned and billable stay whatever the API already stored. Actual is the
 * closed net plus open elapsed (`actualActiveHours`) so a running timer is
 * visible. This module never writes prices or planned hours.
 */

import { formatDuration, sumHours } from './duration';

export interface ActualHoursFields {
  actualDurationHours?: number | null;
  actualActiveHours?: number | null;
}

export interface UnassignedActualHoursFields extends ActualHoursFields {
  unassignedActualDurationHours?: number | null;
  unassignedActualActiveHours?: number | null;
}

export function displayedActualHours(fields: ActualHoursFields | null | undefined): number {
  if (fields == null) return 0;
  return sumHours([fields.actualDurationHours, fields.actualActiveHours]);
}

/**
 * Closed clocked hours only. Open timers are not billed (#2706).
 */
export function closedActualHours(fields: ActualHoursFields | null | undefined): number {
  if (fields == null) return 0;
  const closed = Number(fields.actualDurationHours);
  if (!Number.isFinite(closed) || closed <= 0) return 0;
  return Math.round(closed * 100) / 100;
}

/** Decimal hours for copy such as "Laskuta toteutunut (2,5 h)". */
export function formatDecimalHoursLabel(hours: number): string {
  return Number(hours.toFixed(2)).toString().replace('.', ',');
}

export function hasOpenActualHours(fields: ActualHoursFields | null | undefined): boolean {
  if (fields == null) return false;
  const active = Number(fields.actualActiveHours);
  return Number.isFinite(active) && active > 0;
}

export function actualExceedsPlanned(
  actualHours: number,
  plannedHours: number | null | undefined,
): boolean {
  if (plannedHours == null) return false;
  const planned = Number(plannedHours);
  if (!Number.isFinite(planned)) return false;
  return actualHours > planned;
}

/**
 * List/detail overrun badge: closed actual only, and only when planned is a
 * positive duration so missing/zero planned cannot raise a false warning.
 */
export function hasClosedDurationOverrun(
  plannedHours: number | null | undefined,
  actualDurationHours: number | null | undefined,
): boolean {
  if (plannedHours == null) return false;
  const planned = Number(plannedHours);
  if (!Number.isFinite(planned) || planned <= 0) return false;
  return closedActualHours({ actualDurationHours }) > planned;
}

export function displayedUnassignedActualHours(
  fields: UnassignedActualHoursFields | null | undefined,
): number {
  if (fields == null) return 0;
  return sumHours([fields.unassignedActualDurationHours, fields.unassignedActualActiveHours]);
}

export function formatHoursOrDash(hours: number | null | undefined): string {
  if (hours == null) return '—';
  return formatDuration(hours);
}

export function laborBillableHours(
  laborItems: ReadonlyArray<{ estimatedHours?: number | string | null }> | null | undefined,
): number {
  return sumHours((laborItems ?? []).map((item) => item.estimatedHours));
}

/**
 * Billable hours for a service: labor-item sum. Fixed-price services have no
 * hourly billable quantity (docs/work-order-durations.md).
 */
export function serviceBillableHours(service: {
  pricingType?: string | null;
  laborItems?: ReadonlyArray<{ estimatedHours?: number | string | null }> | null;
}): number | null {
  if (service.pricingType === 'fixed') return null;
  return laborBillableHours(service.laborItems);
}

export function workOrderBillableHours(
  services: ReadonlyArray<{
    pricingType?: string | null;
    laborItems?: ReadonlyArray<{ estimatedHours?: number | string | null }> | null;
  }> | null | undefined,
): number | null {
  const list = services ?? [];
  if (list.length === 0) return null;
  const values = list.map(serviceBillableHours);
  if (values.every((value) => value == null)) return null;
  return sumHours(values);
}

/**
 * Hours to show for one time-entry row. Closed rows use stored duration;
 * open rows use elapsed-to-`now` minus break.
 */
export function timeEntryDisplayHours(
  entry: {
    startedAt: string;
    endedAt: string | null;
    durationHours?: string | number | null;
    breakHours?: string | number | null;
  },
  now: Date,
): number {
  if (entry.endedAt != null) {
    const stored = Number(entry.durationHours);
    return Number.isFinite(stored) && stored > 0 ? Math.round(stored * 100) / 100 : 0;
  }
  const started = new Date(entry.startedAt);
  if (Number.isNaN(started.getTime())) return 0;
  const breakHours = Number(entry.breakHours);
  const breakValue = Number.isFinite(breakHours) ? breakHours : 0;
  const gross = (now.getTime() - started.getTime()) / 3_600_000;
  return Math.max(0, Math.round((gross - breakValue) * 100) / 100);
}
